import { valueToJinja } from './jinja';
import { normalizeDestination } from './destination';
import { fromWidgetState } from '../state/widgetState';
import { APP_NAME } from '../utils/constants';

// Raw-Jinja leaves become quoted sentinels so JSON.stringify still formats cleanly.
export function buildBody(draft, ctx) {
  const { orgId, phoneAttribute, projectId } = ctx;
  const sentinels = [];
  const toSentinel = (text) => {
    const key = `__J${sentinels.length}__`;
    sentinels.push(text);
    return key;
  };

  // Recursively jinja-izes every string leaf in params, headerParams, waTemplateJson.
  const jinjaize = (value) => {
    if (typeof value === 'string') {
      const result = valueToJinja(value);
      return result.raw ? toSentinel(result.text) : value;
    }
    if (Array.isArray(value)) return value.map(jinjaize);
    if (value && typeof value === 'object') {
      const out = {};
      Object.keys(value).forEach((key) => {
        out[key] = jinjaize(value[key]);
      });
      return out;
    }
    return value;
  };

  // A test number goes out as a plain string: Bloomreach's Test webhook renders customer[...] empty.
  const dest = normalizeDestination(draft.destination, phoneAttribute);

  const obj = {
    orgId,
    channelId: draft.channelId,
    integrationId: APP_NAME,
    destination: dest.type === 'number' ? dest.value : toSentinel(`{{ customer['${dest.value}'] | string | json }}`),
    messageType: draft.messageType || 'text',
    templateId: String(draft.templateId),
    type: 'enterprise',
    format: 'json',
    isHSM: true,
    // isTemplate must come from the caller's computed value — wrong value 400s at Enterprise.
    isTemplate: !!draft.isTemplate,
    saveChat: false,
    params: jinjaize(draft.params || []),
  };

  if (draft.headerParams && draft.headerParams.length > 0) {
    obj.headerParams = jinjaize(draft.headerParams);
  }
  if (draft.mediaUrl) obj.mediaUrl = draft.mediaUrl;
  if (draft.fileName) obj.fileName = draft.fileName;
  // name/address defaulted to '' here too (belt-and-suspenders with the Standard panel's own
  // default) — gs-intg's String(undefined) would otherwise send the literal text "undefined".
  if (draft.location) {
    obj.location = jinjaize({
      latitude: draft.location.latitude,
      longitude: draft.location.longitude,
      name: draft.location.name || '',
      address: draft.location.address || '',
    });
  }
  if (draft.waTemplateJson) obj.waTemplateJson = jinjaize(draft.waTemplateJson);
  if (draft.buttonUrlParam) obj.buttonUrlParam = jinjaize(draft.buttonUrlParam);
  if (draft.ctaButtonUrl) obj.ctaButtonUrl = jinjaize(draft.ctaButtonUrl);

  obj.meta = {
    campaignId: toSentinel("{{ scenario['id'] | json }}"),
    campaignName: toSentinel("{{ scenario['name'] | json }}"),
    actionId: toSentinel("{{ action['id'] | json }}"),
    actionName: toSentinel("{{ action['name'] | json }}"),
    actionType: 'whatsapp',
    projectId: projectId || orgId,
    integrationName: 'Gupshup Widget',
    integrationType: 'webhook',
    sentTimestamp: toSentinel('{{ time | json }}'),
  };

  const json = JSON.stringify(obj, null, 2);
  // Function replacement, not string — $-patterns in marketer text would corrupt the JSON.
  return sentinels.reduce((text, sentinelText, index) => text.replace(`"__J${index}__"`, () => sentinelText), json);
}

// The fields every definition shares, in the order they go out, so the placeholder cannot drift from a real one.
const baseDefinition = (body) => ({
  url: process.env.REACT_APP_BLOOMREACH_SEND_URL,
  method: 'POST',
  response_handling: 'json',
  auth: null,
  headers: [{ name: 'Content-Type', value: 'application/json', type: 'public' }],
  body,
  event_properties: {},
  frequency_policy: null,
});

// general_consent ignores consent; consent_category must match one Bloomreach has set.
export function buildDefinition(draft, ctx) {
  const hasConsentCategory = !!ctx.consentCategory;
  return {
    ...baseDefinition(buildBody(draft, ctx)),
    consent_category: hasConsentCategory ? ctx.consentCategory : null,
    general_consent: !hasConsentCategory,
  };
}

// Bloomreach checks consent on every Done, whichever widget tab is open, and a brand-new node has no message
// to hand back, so it would fail with "Please select a consent category." The placeholder asks for general consent
// instead, and its body aborts every send: a scenario that runs with the node still empty tracks an `aborted` campaign
// event per customer with this reason and sends nothing. `{% abort %}` is Bloomreach's own tag, no `| json`.
export const PLACEHOLDER_BODY = '{% abort: "Gupshup message not set up" %}';

export function buildPlaceholderDefinition() {
  return { ...baseDefinition(PLACEHOLDER_BODY), consent_category: null, general_consent: true };
}

// A node Bloomreach saved with the placeholder: it comes back on reopen, with no widget_state.
export const isPlaceholderDefinition = (webhook) => webhook?.body === PLACEHOLDER_BODY;

// A body buildBody built: it always writes the integration id, as `"integrationId": "bloomreach"`.
const BUILT_BODY_MARKER = new RegExp(`"integrationId"\\s*:\\s*"${APP_NAME}"`);

export const isWidgetBuiltDefinition = (webhook) => typeof webhook?.body === 'string' && BUILT_BODY_MARKER.test(webhook.body);

// Whether the widget wrote this node. Consent cannot tell a fresh node from a saved one: Bloomreach opens a fresh node with the
// preset's own webhook, and a preset can ship with general consent (Gupshup's does). Only what the widget wrote can: a widget_state
// of ours (every Save writes one, except past the 4 KB cap, where it goes back null), a body buildBody built (that oversized case),
// or the placeholder (a reopened placeholder goes back as it was).
export const wasSavedByWidget = (webhook, widgetState) =>
  !!fromWidgetState(widgetState) || isPlaceholderDefinition(webhook) || isWidgetBuiltDefinition(webhook);

// Bloomreach refuses a node with no consent decision and keeps the editor open. We use that to stop Done while the screen holds
// unsaved work: the reply carries the last saved node with its decision withheld. Done, Test and Cancel ask for state the same
// way, so we cannot tell them apart. Cancel never validates, so it still leaves cleanly.
export const withoutConsentDecision = (webhook) => ({ ...webhook, consent_category: null, general_consent: false });
